import { Grid, Stack } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { Outlet } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import biApi from "../../../../api/biApi.ts";
import useClientContext from "../../../../context/useClientContext.ts";
import { ReportTemplatesContextProvider } from "./contexts/ReportTemplatesContextProvider.tsx";
import ReportTemplatesUiManager from "./contexts/template/ReportTemplatesUiManager.tsx";
import { GroupsContextProvider } from "./groups/contexts/GroupsContextProvider.tsx";
import useGroups from "./groups/hooks/useGroups.ts";
import useReportTemplates from "./templates/hooks/useReportTemplates";

export default function OrganizationTemplatesPage() {
  const { clientCode } = useClientContext();
  const {
    actions: reportTemplateActions,
    templates,
    ui: templatesUi,
  } = useReportTemplates({ getTemplates: biApi.getOrganizationReportTemplates });
  const { groups, actions: groupActions, ui: groupsUi } = useGroups({ ...biApi.reportTemplateGroup });

  useEffect(() => {
    groupActions.load(clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [clientCode, groupActions.load]);

  if (!templatesUi.loaded || !groupsUi.loaded) {
    return (
      <FullSize>
        <InlineLoader text="Loading report templates..." />
      </FullSize>
    );
  }

  if (templatesUi.errorLoad !== undefined || groupsUi.error !== undefined) {
    return (
      <FullSize>
        <DataLoadingFailed />
      </FullSize>
    );
  }

  return (
    <Grid container sx={{ flex: 1, height: "100%", width: "100%" }}>
      <ReportTemplatesContextProvider actions={reportTemplateActions} reportTemplates={templates} ui={templatesUi}>
        <GroupsContextProvider
          groups={groups}
          ui={groupsUi}
          actions={{ ...groupActions, reloadReports: reportTemplateActions.reloadReportTemplates }}
        >
          <ReportTemplatesUiManager onDeleted={() => reportTemplateActions.reloadReportTemplates()}>
            <Outlet />
          </ReportTemplatesUiManager>
        </GroupsContextProvider>
      </ReportTemplatesContextProvider>
    </Grid>
  );
}

function FullSize({ children }: PropsWithChildren) {
  return <Stack sx={{ height: "100%", width: "100%" }}>{children}</Stack>;
}
